// src/components/PortUsageCard.tsx
import React, { useState, useMemo } from "react";
import { Port } from "./Types";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ServiceUsageChart } from "./ServiceUsageChart";
import { formatDataRate } from "kontrolEdge/helpers/formatDataRate";
import { useAppSelector } from "@redux/hooks";
type PortUsageCardProps = { port: Port; onClick?: (port: Port) => void };

export function PortUsageCard({ port, onClick }: PortUsageCardProps) {
  const { tenantId, partnerId  } = useAppSelector(state => state.selection);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [limitPercent, setLimitPercent] = useState<number>(100);
  const isViewingAllCustomers = tenantId === partnerId;

  // Sum allocated (just internet for now)
  const allocatedSum = useMemo(
    () =>
      Object.values(port.allocated)
        .filter((v) => v != null)
        .reduce((s, v) => s + (v ?? 0), 0),
    [port.allocated]
  );

  // Incoming / outgoing
  const incoming = port.bandwidthConsumption?.incoming ?? 0;
  const outgoing = port.bandwidthConsumption?.outgoing ?? 0;
  const consumption = incoming + outgoing;
  const capacity = port.capacity ?? 0;

  // Effective max = allocatedSum * (limitPercent/100)
  const effectiveMax = useMemo(
    () => (allocatedSum * limitPercent) / 100,
    [allocatedSum, limitPercent]
  );

  const actualState =
    port.state?.actualState ?? port.state?.operationalState ?? "";
  const customerName = port.customer?.name;
  return (
    <div onClick={() => onClick?.(port)}>
    <GenericCard
      isCollapsible={false}
      headerContent={
        <div className="flex-wrap justify-between items-start pr-5">
           <div className="flex-1 pr-4">
            <h3 className="text-md font-bold text-semantic-text-primary leading-tight">
            {isViewingAllCustomers
              ? `(${port?.customer?.name}) Port ${port?.name}`
              : `(${port?.subscriptionId}) Port ${port?.name}`
            }
            </h3>
            <h3 className="font-medium text-semantic-text-secondary">
              Allocated: {formatDataRate(allocatedSum)}
            </h3>
            <h3 className="font-medium text-semantic-text-secondary">
              Capacity: {formatDataRate(capacity)}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2 items-stretch mt-5">
            {isViewingAllCustomers &&   
              <GenericTag
                label={customerName}
                variant="neutral"
              />
            }
            <GenericTag
            label={actualState === "up" ? "Active" : "Inactive"}
            variant={actualState === "up" ? "success" : "error"}
            />
            <GenericTag label={`Services: 1`} variant="default" />
            <GenericTag label={`Tag: ${port?.tag ? port?.tag : '-' }`} variant="default" />
          </div>
        </div>
      }
      bodyContent={
        <>
          <ServiceUsageChart
            serviceName="Enterprise Internet"
            allocated={effectiveMax}
            consumption={consumption}
            incomingValue={incoming}
            outgoingValue={outgoing}
          />
        </>
      }
    />
    </div>
  );
}
